import {
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  BarChart,
  AreaChart,
  Tooltip,
  TooltipProps,
  PieChart,
} from "recharts";
import { ChartTypes } from "./types";
import { ComponentProps, useState } from "react";
import classes from "./chart.module.scss";
import { AxisInterval } from "recharts/types/util/types";
import { FullscreenIcon } from "@assets/icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  IconButton,
  Stack,
} from "@altimateai/lego";
import { yAxisLabelProps } from "./constants";

const ChartsByType = ({
  type,
  children,
  ...rest
}: { type: ChartTypes } & Record<string, unknown>) => {
  switch (type) {
    case ChartTypes.LineChart:
      return <LineChart {...rest}>{children}</LineChart>;
    case ChartTypes.BarChart:
      return <BarChart {...rest}>{children}</BarChart>;
    case ChartTypes.AreaChart:
      return <AreaChart {...rest}>{children}</AreaChart>;
    case ChartTypes.PieChart:
      return <PieChart {...rest}>{children}</PieChart>;
  }
};

const CustomYAxisTick = ({
  x,
  y,
  payload,
  yAxisLabelFormatter,
}: {
  x: number;
  y: number;
  payload: Record<string, string>;
  yAxisLabelFormatter?: (value: string) => string;
}) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="end" dy={5} fill="#66768D">
      {yAxisLabelFormatter ? yAxisLabelFormatter(payload.value) : payload.value}
    </text>
  </g>
);

// Utility to truncate long labels with ellipsis
function truncateLabel(label: string, maxLength = 12) {
  return label.length > maxLength ? label.slice(0, maxLength) + "…" : label;
}

const CustomXAxisTick = ({
  x,
  y,
  payload,
  xAxisLabelFormatter,
}: {
  x?: number;
  y?: number;
  payload?: Record<string, string>;
  xAxisLabelFormatter?: (value: string) => string;
}) =>
  payload ? (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dx={0}
        dy={4}
        transform="rotate(90)"
        textAnchor="start"
        fill="#66768D"
        style={{ fontSize: 12 }}
      >
        <title>{xAxisLabelFormatter ? xAxisLabelFormatter(payload.value) : payload.value}</title>
        {xAxisLabelFormatter
          ? truncateLabel(xAxisLabelFormatter(payload.value))
          : truncateLabel(payload.value)}
      </text>
    </g>
  ) : null;

const getXAxisInterval = (length: number, interval?: AxisInterval) => {
  if (interval) {
    return interval;
  }
  return length > 100 ? Math.round(length / 10) : 0;
};

const ChartComponent = ({
  data,
  type,
  children,
  xAxisDataKey,
  yAxisId,
  yAxisLabel,
  xAxisLabelFormatter,
  yAxisLabelFormatter,
  tooltipProps,
  height = 400,
  xAxisInterval,
  chartProps = {},
  legendProps,
  adaptiveBarSize = false,
  xAxisHeight,
}: Props) => {
  const [selectedLegend, setSelectedLegend] = useState<string | undefined>();
  const [barSize, setBarSize] = useState(7);
  const onLegendClick = (legend?: string) => {
    setSelectedLegend(prev => (prev === legend ? "" : legend));
  };

  const onRender = (ref: HTMLDivElement) => {
    if (ref) {
      const width = ref.getBoundingClientRect().width;
      const bandWidth = width / (data?.length || 1);
      // Default keeps a fixed 10px gap. adaptiveBarSize keeps that gap on wide
      // bands but falls back to a proportional gap on narrow bands so the bar
      // width stays positive at any point count.
      setBarSize(
        adaptiveBarSize
          ? Math.max(1, Math.floor(Math.max(bandWidth - 10, bandWidth * 0.8)))
          : bandWidth - 10
      );
    }
  };

  return (
    <ResponsiveContainer width="100%" height="100%" ref={onRender} minHeight={height || 400}>
      <ChartsByType
        type={type}
        data={data}
        height={height}
        margin={{
          top: 25,
          bottom: 25,
          right: 5,
        }}
        {...chartProps}
      >
        {type === ChartTypes.PieChart ? null : (
          <>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey={xAxisDataKey}
              interval={getXAxisInterval((data || []).length, xAxisInterval)}
              // Recharts reserves only 30px by default, which can't contain the
              // rotated custom ticks; consumers with long/rotated labels pass an
              // explicit band height. Undefined leaves the recharts default.
              height={xAxisHeight}
              tick={<CustomXAxisTick xAxisLabelFormatter={xAxisLabelFormatter} />}
            />
            <YAxis
              yAxisId={yAxisId}
              label={{
                ...yAxisLabelProps,
                ...yAxisLabel,
              }}
              tick={
                //   @ts-expect-error other values will be injected
                <CustomYAxisTick yAxisLabelFormatter={yAxisLabelFormatter} />
              }
            />
          </>
        )}
        <Tooltip {...tooltipProps} />

        <Legend
          align="center"
          verticalAlign="bottom"
          wrapperStyle={{ bottom: -24 }}
          onClick={data => onLegendClick(data.dataKey as string)}
          formatter={value => <span className={classes.legend}>{value}</span>}
          {...legendProps}
        />
        {children?.({ barSize, selectedLegend })}
      </ChartsByType>
    </ResponsiveContainer>
  );
};

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any[];
  type: ChartTypes;
  children?: ({
    barSize,
    selectedLegend,
  }: {
    barSize: number;
    selectedLegend?: string;
  }) => JSX.Element;
  xAxisLabelFormatter?: (value: string) => string;
  yAxisLabelFormatter?: (value: string) => string;
  xAxisDataKey: string;
  yAxisId?: string;
  yAxisLabel?: object;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipProps?: TooltipProps<any, any>;
  title: string;
  height?: number;
  xAxisInterval?: AxisInterval;
  hideFullscreenButton?: boolean;
  chartProps?: Record<string, unknown>;
  legendProps?: Record<string, unknown>;
  // Size bars proportional to the category band so they stay visible when the
  // point count is high (dense daily data). Defaults to a fixed 10px gap.
  adaptiveBarSize?: boolean;
  // Explicit XAxis band height, in px, for charts with rotated/long labels that
  // overflow the recharts default. Undefined uses the recharts default.
  xAxisHeight?: number;
  // props of iconbutton component
  fullScreenProps?: ComponentProps<typeof IconButton>;
}

const Chart = (props: Props) => {
  const [fullscreen, setFullscreen] = useState(false);

  const handleFullScreenClick = () => {
    setFullscreen(prev => !prev);
  };

  return (
    <>
      {fullscreen ? (
        <Dialog open={fullscreen} onOpenChange={open => !open && handleFullScreenClick()}>
          <DialogContent className="al-min-w-[95vw] al-min-h-[95vh] !al-z-[100]">
            <DialogHeader>
              <DialogTitle>{props.title}</DialogTitle>
            </DialogHeader>
            <ChartComponent {...props} />
          </DialogContent>
        </Dialog>
      ) : null}
      <div
        className={classes.chart}
        style={{ minHeight: props.height || 400 }}
        aria-label={`${props.title} chart`}
      >
        <ChartComponent {...props} />
        {props.hideFullscreenButton ? null : (
          <IconButton
            variant="ghost"
            onClick={handleFullScreenClick}
            {...props.fullScreenProps}
            title={props.fullScreenProps?.title || "Show full screen"}
            className={`!al-p-0 ${classes.fullscreen} ${props.fullScreenProps?.className}`}
          >
            <FullscreenIcon className="!al-w-[24px] !al-h-[24px]" />
          </IconButton>
        )}
        {props.data?.length ? null : (
          <Stack className={classes.no_data} aria-label="No data available">
            No data available
          </Stack>
        )}
      </div>
    </>
  );
};

export default Chart;
